/**
 * MutuelleTab — Mutuelle santé & Ayants droit.
 *
 *  - Section 1 : Config mutuelle (assureur, formule, cotisations) + bouton IA
 *  - Section 2 : Ayants droit (CRUD conjoint/enfants/pacs/ascendants)
 *  - Section 3 : Résumé mensuel (part salariale / employeur / total)
 */
import { useEffect, useMemo, useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Heart, Sparkles, Upload, Loader2, CheckCircle2, XCircle,
  Users, User, Baby, UserPlus, Save, Trash2, Download, HeartPulse, Building2,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const FORMULA_LABEL = {
  individual: "Isolé (individuel)",
  couple: "Duo (couple)",
  family: "Famille",
  family_plus: "Famille +",
};

const KIND_LABEL = {
  spouse: "Conjoint(e)",
  pacs: "Pacsé(e)",
  concubin: "Concubin(e)",
  child: "Enfant",
  ascendant: "Ascendant",
};

const KIND_BADGE = {
  spouse: "bg-pink-100 text-pink-700 border-pink-200",
  pacs: "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200",
  concubin: "bg-purple-100 text-purple-700 border-purple-200",
  child: "bg-sky-100 text-sky-700 border-sky-200",
  ascendant: "bg-amber-100 text-amber-700 border-amber-200",
};

const KIND_ICON = { spouse: User, pacs: User, concubin: User, child: Baby, ascendant: Users };

const fmtEUR = (v) => v == null
  ? "—"
  : `${Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const fmtDate = (v) => v ? new Date(v).toLocaleDateString("fr-FR") : "—";

// ─────────────────────────────────────────────────────────────
// Wizard IA import contrat mutuelle
// ─────────────────────────────────────────────────────────────

function AIImportDialog({ open, onOpenChange, employeeId, onDone }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({});
  const [deps, setDeps] = useState([]);

  const reset = () => {
    setFile(null); setResult(null); setForm({}); setDeps([]);
    setDetecting(false); setApplying(false);
  };

  const runDetect = async () => {
    if (!file) { toast.error("Dépose le PDF"); return; }
    setDetecting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/hr/employees/${employeeId}/mutuelle/ai-import`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      setForm({
        insurer_name: r.data.insurer_name || "",
        contract_reference: r.data.contract_reference || "",
        formula: r.data.formula || "individual",
        adhesion_date: r.data.adhesion_date || "",
        end_date: r.data.end_date || "",
        is_mandatory: r.data.is_mandatory ?? true,
        total_monthly: r.data.amounts?.total_monthly ?? null,
        employee_monthly: r.data.amounts?.employee_monthly ?? null,
        employer_monthly: r.data.amounts?.employer_monthly ?? null,
        employee_rate_pct: r.data.amounts?.employee_rate_pct ?? null,
        employer_rate_pct: r.data.amounts?.employer_rate_pct ?? null,
        base: r.data.amounts?.base || "",
        options: r.data.options || [],
      });
      setDeps((r.data.dependents || []).map(d => ({ ...d, _selected: true })));
    } catch (e) { toast.error(apiError(e, "Détection IA échouée")); }
    finally { setDetecting(false); }
  };

  const apply = async () => {
    if (!form.insurer_name || !form.employee_monthly) {
      toast.error("Assureur et cotisation salariale requis"); return;
    }
    setApplying(true);
    try {
      const create_dependents = deps.filter(d => d._selected).map(d => ({
        kind: d.kind, first_name: d.first_name, last_name: d.last_name,
        birth_date: d.birth_date, ss_number: d.ss_number,
        is_covered_mutuelle: true, is_dependent_tax: false,
      }));
      await api.post(`/hr/employees/${employeeId}/mutuelle/ai-import/apply`, {
        upload_id: result.upload_id, payload: form, create_dependents,
      });
      toast.success(`Mutuelle enregistrée · ${create_dependents.length} ayant(s) droit ajouté(s)`);
      onDone?.(); reset(); onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setApplying(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
                      data-testid="ai-mutuelle-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            Importer une mutuelle avec l'IA
          </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-2 space-y-3">
          {!result && (
            <>
              <Alert className="bg-blue-50 border-blue-200">
                <Sparkles className="w-4 h-4 text-blue-600" />
                <AlertDescription className="text-xs text-blue-900">
                  Dépose l'adhésion, certificat ou contrat mutuelle. L'IA extrait
                  <strong> assureur, formule, cotisations part salariale / employeur, ayants droit</strong>.
                </AlertDescription>
              </Alert>
              <div
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer?.files?.[0]; if (f) setFile(f); }}
                className={cn("border-2 border-dashed rounded-xl p-6 cursor-pointer transition",
                  file ? "border-emerald-400 bg-emerald-50/40"
                       : "border-slate-300 bg-slate-50/40 hover:border-blue-400 hover:bg-blue-50/40")}
                data-testid="drop-mutuelle"
              >
                <input id="mutuelle-file-input" name="mutuelle-file-input" ref={inputRef} type="file" className="hidden"
                  accept="application/pdf,image/png,image/jpeg,image/webp"
                  onChange={(e) => e.target.files?.[0] && setFile(e.target.files[0])} />
                <div className="flex items-center gap-3">
                  {file ? <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                        : <Upload className="w-6 h-6 text-slate-400" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{file ? file.name : "Dépose le PDF"}</p>
                    <p className="text-xs text-slate-500">PDF, JPG, PNG · 8 Mo max</p>
                  </div>
                  {file && (
                    <Button size="sm" variant="ghost"
                      onClick={(e) => { e.stopPropagation(); setFile(null); }}>
                      <XCircle className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}

          {result && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">Extraction · {result.source_filename}</span>
                <Badge variant="outline" className="bg-purple-50 border-purple-200 text-purple-700">
                  Confiance {Math.round((result.confidence || 0) * 100)}%
                </Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-slate-500">Assureur *</Label>
                  <Input value={form.insurer_name || ""}
                    onChange={(e) => setForm(p => ({ ...p, insurer_name: e.target.value }))}
                    data-testid="mut-insurer" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-500">N° contrat</Label>
                  <Input value={form.contract_reference || ""}
                    onChange={(e) => setForm(p => ({ ...p, contract_reference: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-500">Formule</Label>
                  <Select value={form.formula || "individual"}
                    onValueChange={(v) => setForm(p => ({ ...p, formula: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(FORMULA_LABEL).map(([v, l]) =>
                        <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between border rounded-lg px-3 py-2">
                  <Label className="text-xs">Adhésion obligatoire (ANI 2013)</Label>
                  <Switch checked={!!form.is_mandatory}
                    onCheckedChange={(v) => setForm(p => ({ ...p, is_mandatory: v }))} />
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-500">Date d'adhésion</Label>
                  <Input type="date" value={form.adhesion_date || ""}
                    onChange={(e) => setForm(p => ({ ...p, adhesion_date: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-500">Fin</Label>
                  <Input type="date" value={form.end_date || ""}
                    onChange={(e) => setForm(p => ({ ...p, end_date: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-500">Cotisation salariale (€/mois) *</Label>
                  <Input type="number" step="0.01" value={form.employee_monthly ?? ""}
                    onChange={(e) => setForm(p => ({ ...p, employee_monthly: e.target.value === "" ? null : parseFloat(e.target.value) }))}
                    data-testid="mut-employee" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-500">Cotisation employeur (€/mois)</Label>
                  <Input type="number" step="0.01" value={form.employer_monthly ?? ""}
                    onChange={(e) => setForm(p => ({ ...p, employer_monthly: e.target.value === "" ? null : parseFloat(e.target.value) }))} />
                </div>
              </div>

              {deps.length > 0 && (
                <div className="border rounded-lg p-3 bg-slate-50">
                  <p className="text-xs font-medium mb-2">
                    Ayants droit détectés — coche ceux à créer :
                  </p>
                  <div className="space-y-1.5">
                    {deps.map((d, i) => (
                      <label key={i} className="flex items-center gap-2 p-2 bg-white rounded border cursor-pointer">
                        <input id={`dep-check-${i}`} name={`dep-check-${i}`} type="checkbox" checked={d._selected}
                          onChange={() => setDeps(prev => prev.map((x, j) => j === i ? { ...x, _selected: !x._selected } : x))}
                          data-testid={`dep-check-${i}`} />
                        <Badge variant="outline" className={KIND_BADGE[d.kind]}>
                          {KIND_LABEL[d.kind]}
                        </Badge>
                        <span className="text-sm font-medium">{d.first_name} {d.last_name}</span>
                        {d.birth_date && (
                          <span className="text-xs text-slate-500">né(e) {fmtDate(d.birth_date)}</span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="pt-3 border-t border-slate-100">
          {!result && (
            <>
              <Button variant="ghost" onClick={() => { reset(); onOpenChange(false); }}>Annuler</Button>
              <Button onClick={runDetect} disabled={!file || detecting}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="wizard-mut-detect">
                {detecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            : <Sparkles className="w-4 h-4 mr-2" />}
                Analyser avec l'IA
              </Button>
            </>
          )}
          {result && (
            <>
              <Button variant="outline" onClick={() => { setResult(null); setForm({}); setDeps([]); }}>
                Retour
              </Button>
              <Button onClick={apply} disabled={applying}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="wizard-mut-apply">
                {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          : <HeartPulse className="w-4 h-4 mr-2" />}
                Enregistrer la mutuelle
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// Dialog Ayant droit
// ─────────────────────────────────────────────────────────────

function DependentDialog({ open, onOpenChange, employeeId, existing, onDone }) {
  const [form, setForm] = useState({
    kind: "spouse", first_name: "", last_name: "", birth_date: "",
    ss_number: "", is_dependent_tax: false, is_covered_mutuelle: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (existing) setForm({ ...existing });
    else setForm({ kind: "spouse", first_name: "", last_name: "", birth_date: "",
                    ss_number: "", is_dependent_tax: false, is_covered_mutuelle: true });
  }, [existing, open]);

  const submit = async () => {
    setSaving(true);
    try {
      if (existing?.id) {
        await api.patch(`/hr/dependents/${existing.id}`, form);
        toast.success("Ayant droit mis à jour");
      } else {
        await api.post(`/hr/employees/${employeeId}/dependents`, form);
        toast.success("Ayant droit créé");
      }
      onDone?.(); onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{existing ? "Modifier l'ayant droit" : "Ajouter un ayant droit"}</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase text-slate-500">Type *</Label>
            <Select value={form.kind} onValueChange={(v) => setForm(p => ({ ...p, kind: v }))}>
              <SelectTrigger data-testid="dep-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(KIND_LABEL).map(([v, l]) =>
                  <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase text-slate-500">Prénom *</Label>
              <Input value={form.first_name || ""}
                onChange={(e) => setForm(p => ({ ...p, first_name: e.target.value }))}
                data-testid="dep-first-name" />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Nom *</Label>
              <Input value={form.last_name || ""}
                onChange={(e) => setForm(p => ({ ...p, last_name: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Date naissance</Label>
              <Input type="date" value={form.birth_date || ""}
                onChange={(e) => setForm(p => ({ ...p, birth_date: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">N° sécu</Label>
              <Input value={form.ss_number || ""} className="font-mono text-xs"
                onChange={(e) => setForm(p => ({ ...p, ss_number: e.target.value }))} />
            </div>
          </div>
          <div className="flex items-center justify-between border rounded p-2">
            <Label className="text-xs">Couvert par la mutuelle</Label>
            <Switch checked={!!form.is_covered_mutuelle}
              onCheckedChange={(v) => setForm(p => ({ ...p, is_covered_mutuelle: v }))} />
          </div>
          <div className="flex items-center justify-between border rounded p-2">
            <Label className="text-xs">À charge fiscalement</Label>
            <Switch checked={!!form.is_dependent_tax}
              onCheckedChange={(v) => setForm(p => ({ ...p, is_dependent_tax: v }))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="dep-save">
            {saving ? "..." : existing ? "Enregistrer" : "Créer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// Vue principale
// ─────────────────────────────────────────────────────────────

export default function MutuelleTab({ employeeId, canWrite }) {
  const [mutuelle, setMutuelle] = useState(null);
  const [dependents, setDependents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [depOpen, setDepOpen] = useState(false);
  const [editingDep, setEditingDep] = useState(null);
  const [form, setForm] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [m, d, s] = await Promise.all([
        api.get(`/hr/employees/${employeeId}/mutuelle`),
        api.get(`/hr/employees/${employeeId}/dependents`),
        api.get(`/hr/employees/${employeeId}/mutuelle/summary`),
      ]);
      setMutuelle(m.data.mutuelle);
      setDependents(d.data.dependents || []);
      setSummary(s.data);
      setForm(m.data.mutuelle || {
        insurer_name: "", contract_reference: "", formula: "individual",
        adhesion_date: "", end_date: "", is_mandatory: true,
        employee_monthly: null, employer_monthly: null, total_monthly: null,
      });
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [employeeId]);

  const saveMutuelle = async () => {
    setSaving(true);
    try {
      await api.put(`/hr/employees/${employeeId}/mutuelle`, form);
      toast.success("Mutuelle enregistrée");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const removeDep = async (did) => {
    if (!window.confirm("Supprimer cet ayant droit ?")) return;
    try {
      await api.delete(`/hr/dependents/${did}`);
      toast.success("Supprimé"); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const total = useMemo(() => {
    const s = Number(form.employee_monthly || 0) + Number(form.employer_monthly || 0);
    return s || null;
  }, [form.employee_monthly, form.employer_monthly]);

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-slate-100 rounded animate-pulse" />)}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="mutuelle-tab">
      {/* Résumé mensuel */}
      {mutuelle && (
        <Card className="border-l-4 border-l-pink-500 bg-pink-50/30">
          <CardContent className="pt-4 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Assureur</p>
                <p className="text-sm font-semibold text-slate-900 mt-0.5">
                  {mutuelle.insurer_name || "—"}
                </p>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  {FORMULA_LABEL[mutuelle.formula] || "—"}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Part salariale</p>
                <p className="text-lg font-bold text-pink-700 mt-0.5"
                    data-testid="mut-sum-employee">{fmtEUR(mutuelle.employee_monthly)}</p>
                <p className="text-[10px] text-slate-500">/ mois</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Part employeur</p>
                <p className="text-lg font-bold text-emerald-700 mt-0.5">{fmtEUR(mutuelle.employer_monthly)}</p>
                <p className="text-[10px] text-slate-500">/ mois</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">
                  Ayants droit couverts
                </p>
                <p className="text-lg font-bold text-slate-900 mt-0.5">
                  {summary?.dependents_covered || 0} / {summary?.dependents_total || 0}
                </p>
                <div className="flex gap-1 flex-wrap mt-1">
                  {Object.entries(summary?.dependents_by_kind || {}).map(([k, v]) => (
                    <Badge key={k} variant="outline" className={cn("text-[10px]", KIND_BADGE[k])}>
                      {v} {KIND_LABEL[k]}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section 1 : Config mutuelle */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Heart className="w-4 h-4 text-pink-600" /> Contrat mutuelle santé
          </CardTitle>
          {canWrite && (
            <Button size="sm" variant="outline"
              className="border-purple-300 text-purple-700 hover:bg-purple-50"
              onClick={() => setAiOpen(true)}
              data-testid="ai-mutuelle-btn">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Importer avec IA
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase text-slate-500">Assureur</Label>
              <Input value={form.insurer_name || ""} disabled={!canWrite}
                onChange={(e) => setForm(p => ({ ...p, insurer_name: e.target.value }))}
                placeholder="Malakoff Humanis, AG2R, Harmonie…"
                data-testid="form-insurer" />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">N° contrat</Label>
              <Input value={form.contract_reference || ""} disabled={!canWrite}
                onChange={(e) => setForm(p => ({ ...p, contract_reference: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Formule</Label>
              <Select value={form.formula || "individual"} disabled={!canWrite}
                onValueChange={(v) => setForm(p => ({ ...p, formula: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(FORMULA_LABEL).map(([v, l]) =>
                    <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between border rounded-lg px-3 py-2">
              <Label className="text-xs">Adhésion obligatoire (ANI 2013)</Label>
              <Switch checked={!!form.is_mandatory} disabled={!canWrite}
                onCheckedChange={(v) => setForm(p => ({ ...p, is_mandatory: v }))} />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Date d'adhésion</Label>
              <Input type="date" value={form.adhesion_date || ""} disabled={!canWrite}
                onChange={(e) => setForm(p => ({ ...p, adhesion_date: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Fin (si CDD)</Label>
              <Input type="date" value={form.end_date || ""} disabled={!canWrite}
                onChange={(e) => setForm(p => ({ ...p, end_date: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Cotisation salariale (€/mois)</Label>
              <Input type="number" step="0.01" value={form.employee_monthly ?? ""} disabled={!canWrite}
                onChange={(e) => setForm(p => ({ ...p, employee_monthly: e.target.value === "" ? null : parseFloat(e.target.value) }))} />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Cotisation employeur (€/mois)</Label>
              <Input type="number" step="0.01" value={form.employer_monthly ?? ""} disabled={!canWrite}
                onChange={(e) => setForm(p => ({ ...p, employer_monthly: e.target.value === "" ? null : parseFloat(e.target.value) }))} />
            </div>
          </div>

          {total && (
            <div className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded">
              <span className="text-slate-600">Total mensuel</span>
              <span className="font-semibold text-slate-900">{fmtEUR(total)}</span>
            </div>
          )}

          {canWrite && (
            <div className="flex items-center justify-between pt-2">
              {mutuelle?.file_asset_id && (
                <a href={`/api/hr/mutuelle/files/${mutuelle.file_asset_id}`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900">
                  <Download className="w-3.5 h-3.5" /> Télécharger le contrat
                </a>
              )}
              <Button onClick={saveMutuelle} disabled={saving} className="ml-auto"
                data-testid="save-mutuelle-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        : <Save className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 2 : Ayants droit */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-700" /> Ayants droit
            <Badge variant="outline" className="ml-2 bg-white">{dependents.length}</Badge>
          </CardTitle>
          {canWrite && (
            <Button size="sm" onClick={() => { setEditingDep(null); setDepOpen(true); }}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="add-dependent-btn">
              <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Ajouter
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {dependents.length === 0 ? (
            <div className="p-6 text-center">
              <Users className="w-8 h-8 mx-auto text-slate-300 mb-1" />
              <p className="text-sm text-slate-500">Aucun ayant droit enregistré</p>
            </div>
          ) : (
            <div className="divide-y">
              {dependents.map((d, i) => {
                const Icon = KIND_ICON[d.kind] || User;
                return (
                  <div key={d.id} className="p-3 hover:bg-slate-50 flex items-center justify-between"
                        data-testid={`dep-row-${i}`}>
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={cn("w-9 h-9 rounded-full flex items-center justify-center border",
                        KIND_BADGE[d.kind])}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900">
                          {d.first_name} {d.last_name}
                        </div>
                        <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5">
                          <Badge variant="outline" className={cn("text-[10px]", KIND_BADGE[d.kind])}>
                            {KIND_LABEL[d.kind]}
                          </Badge>
                          {d.birth_date && <span>né(e) {fmtDate(d.birth_date)}</span>}
                          {d.is_covered_mutuelle && (
                            <span className="text-pink-700">· Couvert mutuelle</span>
                          )}
                          {d.is_dependent_tax && (
                            <span className="text-emerald-700">· À charge fiscale</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {canWrite && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost"
                          onClick={() => { setEditingDep(d); setDepOpen(true); }}>
                          Modifier
                        </Button>
                        <Button size="sm" variant="ghost"
                          onClick={() => removeDep(d.id)}
                          className="text-red-600 hover:bg-red-50"
                          data-testid={`dep-delete-${i}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <AIImportDialog open={aiOpen} onOpenChange={setAiOpen}
        employeeId={employeeId} onDone={load} />
      <DependentDialog open={depOpen} onOpenChange={setDepOpen}
        employeeId={employeeId} existing={editingDep} onDone={load} />
    </div>
  );
}
